/**
 * FactorSidebarMolecule.tsx
 *
 * Left sidebar component for the status factors master/detail layout.
 * Lists all configured factors with selection, status badges, and add button.
 * Includes a collapsible section for deactivated factors.
 */

import React, { useState } from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import FactorListItemAtom from '../../atoms/FactorListItem/FactorListItemAtom';
import type { LocalFactor } from '../../../types/statusFactors';
import './FactorSidebarMolecule.scss';

export interface FactorSidebarMoleculeProps {
  /** List of active factors to display */
  factors: LocalFactor[];
  /** List of deactivated factors */
  deactivatedFactors?: LocalFactor[];
  /** Currently selected factor ID */
  selectedFactorId: string | null;
  /** Number of factors with unsaved changes */
  dirtyFactorCount: number;
  /** Whether user has permission to add/edit factors */
  hasEditPermission: boolean;
  /** Whether a save operation is in progress */
  isSaving?: boolean;
  /** Callback when a factor is selected */
  onSelectFactor: (factorId: string | null) => void;
  /** Callback when Add Factor button is clicked */
  onAddFactor: () => void;
  /** Additional CSS class names */
  className?: string;
}

const FactorSidebarMolecule: React.FC<FactorSidebarMoleculeProps> = ({
  factors,
  deactivatedFactors = [],
  selectedFactorId,
  dirtyFactorCount,
  hasEditPermission,
  isSaving = false,
  onSelectFactor,
  onAddFactor,
  className,
}) => {
  const { t } = useTranslation();
  const [deactivatedExpanded, setDeactivatedExpanded] = useState(false);

  const classes = ['factor-sidebar', className].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {/* Header */}
      <div className="factor-sidebar__header">
        <div className="factor-sidebar__title">
          <span className="factor-sidebar__title-text">
            {t('statusFactors.sidebar.title', 'Factors')}
          </span>
          <span className="factor-sidebar__count">({factors.length})</span>
        </div>
        {hasEditPermission && (
          <ButtonAtom
            type="button"
            icon="pi pi-plus"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              onAddFactor();
            }}
            variant="text"
            size="small"
            className="factor-sidebar__add-btn"
            title={t('statusFactors.actions.add', 'Add Factor')}
            disabled={isSaving}
          />
        )}
      </div>

      {/* Factor list */}
      <div className="factor-sidebar__list">
        {factors.length === 0 ? (
          <div className="factor-sidebar__empty">
            <i className="pi pi-inbox" />
            <p>{t('statusFactors.sidebar.empty', 'No factors configured')}</p>
            {hasEditPermission && (
              <ButtonAtom
                type="button"
                label={t('statusFactors.actions.addFirst', 'Add your first factor')}
                icon="pi pi-plus"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onAddFactor();
                }}
                variant="secondary"
                size="small"
                disabled={isSaving}
              />
            )}
          </div>
        ) : (
          factors.map((factor) => (
            <FactorListItemAtom
              key={factor.id}
              id={factor.id}
              name={factor.localData.name || getDefaultName(factor.localData.factorType, t)}
              factorType={factor.localData.factorType}
              status={factor.status}
              selected={factor.id === selectedFactorId}
              isDirty={factor.isDirty}
              hasErrors={factor.hasValidationErrors && !!factor.localData.factorType}
              onClick={onSelectFactor}
            />
          ))
        )}
      </div>

      {/* Deactivated factors section */}
      {deactivatedFactors.length > 0 && (
        <div className="factor-sidebar__deactivated">
          <button
            type="button"
            className="factor-sidebar__deactivated-header"
            onClick={() => setDeactivatedExpanded((prev) => !prev)}
            aria-expanded={deactivatedExpanded}
          >
            <i className={`pi ${deactivatedExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} />
            <span className="factor-sidebar__deactivated-title">
              {t('statusFactors.deactivated.sectionTitle')}
            </span>
            <span className="factor-sidebar__deactivated-count">
              ({t('statusFactors.deactivated.count', { count: deactivatedFactors.length })})
            </span>
          </button>
          {deactivatedExpanded && (
            <div className="factor-sidebar__deactivated-list">
              {deactivatedFactors.map((factor) => (
                <FactorListItemAtom
                  key={factor.id}
                  id={factor.id}
                  name={factor.localData.name || getDefaultName(factor.localData.factorType, t)}
                  factorType={factor.localData.factorType}
                  status="deactivated"
                  selected={factor.id === selectedFactorId}
                  onClick={onSelectFactor}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer with dirty status */}
      {dirtyFactorCount > 0 && (
        <div className="factor-sidebar__footer">
          <div className="factor-sidebar__dirty-status">
            <i className="pi pi-exclamation-circle" />
            <span>
              {dirtyFactorCount === 1
                ? t('statusFactors.sidebar.unsavedSingle', '1 unsaved change')
                : t('statusFactors.sidebar.unsavedPlural', '{{count}} unsaved changes', {
                    count: dirtyFactorCount,
                  })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Get default display name for a factor type
 */
function getDefaultName(
  factorType: string | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any
): string {
  switch (factorType) {
    case 'manual':
      return t('statusFactors.types.manual.label', 'Manual Check-In');
    case 'related_spheres':
      return t('statusFactors.types.relatedSpheres.label', 'Related Spheres');
    case 'arc_health':
      return t('statusFactors.types.arcHealth.label');
    default:
      return t('statusFactors.types.new.label', 'New Factor');
  }
}

FactorSidebarMolecule.displayName = 'FactorSidebarMolecule';

export default FactorSidebarMolecule;
